/**
 * Les cinq onglets de la fiche enfant côté équipe : les questions du
 * formulaire des parents (`components/forms/kid/Etape*.tsx`), dans le même
 * ordre et avec les mêmes mots, mais dans les champs compacts de la gestion.
 *
 * Les écrans des parents ne sont pas réutilisés tels quels : ils lisent le
 * compte connecté (« Même adresse que moi », les personnes autorisées du
 * parent connecté), qui est ici celui d'un membre de l'équipe.
 */

import type { ReactNode } from 'react'
import { CheckCircle, ShieldCheck } from 'lucide-react'
import { chiffresNRN } from '../../../../lib/registreNational'
import {
  classeAChoisir,
  classesDeLEcole,
  ecolesSemblables,
  optionsDeClasse,
  type ClassesConnues,
} from '../../../../lib/classes/regles'
import { libelleAnneeScolaire } from '../../../../lib/jpa/regles'
import { DESCRIPTIONS, type Reponse } from '../../../../lib/droitImage'
import { VERSION_POLITIQUE } from '../../../../lib/confidentialite'
import { ACCORD_SOINS } from '../../../../components/forms/kid/textes'
import {
  NIVEAUX_NATATION,
  PHRASE_NATATION,
  nrnRetenu,
  type Autorisations,
  type Identite,
  type NiveauNatation,
  type Sante,
  type Sorties,
  type VieEnStage,
} from '../../../../components/forms/kid/fiche'
import { libelleConsentement, type AnciennesReponses, type ReperesEquipe } from './regles'
import {
  AncienneReponse, Aide, Case, Interrupteur, Libelle, Liste, Message, OuiNon, Saisie, Section, ZoneTexte,
  type Messages,
} from './champs'

interface PropsOnglet<T> {
  valeur: T
  onChange: (v: T) => void
  messages: Messages
  /** Le prénom de l'enfant, pour dire les questions comme au parent. */
  enfant: string
}

const dateFr = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' })

/* -------------------------------------------------------------------------- */
/* Identité et école                                                           */
/* -------------------------------------------------------------------------- */

export interface Ecole {
  id: string
  name: string
  code_postal: string | null
  active: boolean | null
}

export function OngletIdentite({
  valeur, onChange, messages, ecoles, classes, annee, ecoleEnregistree, classeEnregistree, adresseParent,
}: PropsOnglet<Identite> & {
  /** Toutes les écoles, actives ou non : celle d'un enfant doit toujours s'afficher. */
  ecoles: Ecole[]
  classes?: ClassesConnues
  annee: number
  /** L'école en base : y revenir rend la classe enregistrée. */
  ecoleEnregistree: string
  /** La classe en base : une classe fermée reste affichée si c'est la sienne. */
  classeEnregistree: string
  adresseParent: { adresse: string; cPostal: string; localite: string } | null
}) {
  const maj = (champs: Partial<Identite>) => onChange({ ...valeur, ...champs })
  const nrn = nrnRetenu(valeur)
  const erreurNrn = messages.bloquantes.nNational || messages.manques.nNational

  const optionsEcole = ecoles.map(e => ({
    valeur: e.id,
    libelle: `${e.name}${e.code_postal ? ` (${e.code_postal})` : ''}${e.active === false ? ' — désactivée' : ''}`,
  }))
  if (valeur.ecole && valeur.ecole !== 'other' && !ecoles.some(e => e.id === valeur.ecole)) {
    optionsEcole.unshift({ valeur: valeur.ecole, libelle: 'École introuvable dans la liste' })
  }
  optionsEcole.push({ valeur: 'other', libelle: 'Autre / école non listée' })

  const semblables = valeur.ecole === 'other' ? ecolesSemblables(valeur.ecoleAutre, ecoles) : []
  const demanderClasse = !!classes && classeAChoisir(valeur.ecole, classes)
  const memeAdresse = !!adresseParent
    && adresseParent.adresse === valeur.adresse.trim()
    && adresseParent.cPostal === valeur.cPostal.trim()
    && adresseParent.localite === valeur.localite.trim()

  return (
    <div className="space-y-8">
      <Section titre="Identité">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Saisie label="Prénom" requis valeur={valeur.prenom} onChange={v => maj({ prenom: v })} cle="prenom" messages={messages} />
          <Saisie label="Nom" requis valeur={valeur.nom} onChange={v => maj({ nom: v })} cle="nom" messages={messages} />
          <Saisie
            label="Date de naissance" requis type="date"
            valeur={valeur.dateNaissance} onChange={v => maj({ dateNaissance: v })}
            cle="dateNaissance" messages={messages}
          />
          <div>
            <Saisie
              label="Numéro de registre national"
              valeur={valeur.nNational}
              onChange={v => maj({ nNational: v })}
              placeholder="00.00.00-000.00"
              cle="nNational"
              messages={messages}
            />
            {!nrn && (
              <Aide>Facultatif. Sans lui, pas d’attestation fiscale ; il n’empêche aucune inscription.</Aide>
            )}
            {nrn && !erreurNrn && (
              <p className="mt-1 inline-flex items-center gap-1 text-xs text-green-700">
                <CheckCircle className="h-3.5 w-3.5" /> Numéro valide (clé et date de naissance)
              </p>
            )}
            {nrn && erreurNrn && (
              <button
                type="button"
                onClick={() => maj({ nNational: '' })}
                className="mt-1 text-xs font-medium text-amber-700 underline hover:text-amber-800"
              >
                Retirer ce numéro
              </button>
            )}
            {nrn && chiffresNRN(valeur.nNational).length !== 11 && !erreurNrn && (
              <Aide>11 chiffres attendus.</Aide>
            )}
          </div>
        </div>
      </Section>

      <Section titre="Adresse" intro="Celle de l’enfant, qui peut différer de celle du parent : elle détermine le tarif local.">
        {adresseParent && !memeAdresse && (
          <button
            type="button"
            onClick={() => maj(adresseParent)}
            className="text-sm font-medium text-amber-700 underline hover:text-amber-800"
          >
            Reprendre l’adresse du parent ({adresseParent.adresse}, {adresseParent.cPostal} {adresseParent.localite})
          </button>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Saisie
            label="Adresse" requis className="sm:col-span-2" placeholder="Rue et numéro"
            valeur={valeur.adresse} onChange={v => maj({ adresse: v })} cle="adresse" messages={messages}
          />
          <Saisie
            label="Code postal" requis placeholder="1030"
            valeur={valeur.cPostal} onChange={v => maj({ cPostal: v.replace(/\D/g, '').slice(0, 4) })}
            cle="cPostal" messages={messages}
          />
          <Saisie
            label="Localité" requis placeholder="Schaerbeek"
            valeur={valeur.localite} onChange={v => maj({ localite: v })} cle="localite" messages={messages}
          />
        </div>
      </Section>

      <Section titre="École et classe">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Liste
            label="École" requis
            valeur={valeur.ecole}
            onChange={ecole => maj({ ecole, classeId: ecole === ecoleEnregistree ? classeEnregistree : '' })}
            vide="Sélectionner une école"
            options={optionsEcole}
            cle="ecole" messages={messages}
            aide="L’école détermine le tarif local et l’accès aux journées pédagogiques."
          />
          {valeur.ecole === 'other' && (
            <Saisie
              label="Nom de l’école" requis placeholder="Nom complet de l’établissement"
              valeur={valeur.ecoleAutre} onChange={v => maj({ ecoleAutre: v })}
              cle="ecoleAutre" messages={messages}
              aide="Sans une école de la liste, l’enfant n’a ni tarif local ni accès aux journées pédagogiques de son école."
            />
          )}
          {semblables.length > 0 && (
            <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-medium text-gray-900">Est-ce l’une de ces écoles ?</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {semblables.map(e => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => maj({ ecole: e.id, ecoleAutre: '', classeId: '' })}
                    className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100"
                  >
                    {e.name}{e.code_postal ? ` (${e.code_postal})` : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
          {demanderClasse && classes && (
            <Liste
              label={`Classe en ${libelleAnneeScolaire(annee)}`}
              valeur={valeur.classeId}
              onChange={v => maj({ classeId: v })}
              vide="— À indiquer —"
              options={optionsDeClasse(classesDeLEcole(valeur.ecole, classes), classeEnregistree)}
              cle="classeId" messages={messages}
              aide="Les journées pédagogiques et le parascolaire de l’école sont ouverts classe par classe ; la classe se redemande à chaque rentrée. Une classe manque à la liste ? Elle s’ajoute avec le centre de l’école (Planification › Lieux › Centres)."
            />
          )}
        </div>
      </Section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Santé et alimentation                                                       */
/* -------------------------------------------------------------------------- */

export function OngletSante({
  valeur, onChange, messages, enfant, reperes, onReperes,
}: PropsOnglet<Sante> & { reperes: ReperesEquipe; onReperes: (r: ReperesEquipe) => void }) {
  const maj = (champs: Partial<Sante>) => onChange({ ...valeur, ...champs })
  return (
    <div className="space-y-8">
      <Section titre="Ce que l’équipe doit savoir">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ZoneTexte label="Informations médicales importantes" valeur={valeur.infosMedicales} onChange={v => maj({ infosMedicales: v })} />
          <ZoneTexte label="Antécédents médicaux" valeur={valeur.antecedents} onChange={v => maj({ antecedents: v })} />
        </div>
      </Section>

      <Section titre="Médicaments">
        <Interrupteur
          label={`${enfant} doit prendre un médicament pendant le stage`}
          coche={valeur.medication} onChange={v => maj({ medication: v })}
        />
        {valeur.medication && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ZoneTexte
              label="Lequel, et à quelle posologie" requis
              valeur={valeur.medicationDetails} onChange={v => maj({ medicationDetails: v })}
              cle="medicationDetails" messages={messages}
            />
            <Interrupteur
              label="Peut le prendre sans l’aide d’un adulte"
              coche={valeur.medicationAutonomie} onChange={v => maj({ medicationAutonomie: v })}
            />
            <Interrupteur
              label="Formulaire de médication envoyé"
              coche={reperes.formulaireMedication}
              onChange={v => onReperes({ ...reperes, formulaireMedication: v })}
              aide="Repère de l’équipe, que le parent ne voit pas. Il repasse à « non » quand le traitement change."
            />
          </div>
        )}
        <Interrupteur label="Vaccin contre le tétanos à jour" coche={valeur.tetanos} onChange={v => maj({ tetanos: v })} />
      </Section>

      <Section titre="Son médecin">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Saisie
            label="Nom du médecin traitant" requis
            valeur={valeur.medecinNom} onChange={v => maj({ medecinNom: v })} cle="medecinNom" messages={messages}
          />
          <Saisie label="Téléphone du médecin" type="tel" valeur={valeur.medecinTel} onChange={v => maj({ medecinTel: v })} />
        </div>
      </Section>

      <Section titre="Allergies et alimentation">
        <Interrupteur
          label={`${enfant} a une ou plusieurs allergies`}
          coche={valeur.allergies} onChange={v => maj({ allergies: v })}
        />
        {valeur.allergies && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ZoneTexte
              label="À quoi" requis valeur={valeur.allergiesDetails} onChange={v => maj({ allergiesDetails: v })}
              cle="allergiesDetails" messages={messages}
            />
            <ZoneTexte
              label="Ce qui se passe en cas de contact, et ce qu’il faut faire" requis
              valeur={valeur.allergiesConsequences} onChange={v => maj({ allergiesConsequences: v })}
              cle="allergiesConsequences" messages={messages}
            />
          </div>
        )}
        <Interrupteur
          label={`${enfant} suit un régime alimentaire particulier`}
          coche={valeur.regimeSpecial} onChange={v => maj({ regimeSpecial: v })}
        />
        {valeur.regimeSpecial && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ZoneTexte
              label="Lequel" requis valeur={valeur.regimeDetails} onChange={v => maj({ regimeDetails: v })}
              cle="regimeDetails" messages={messages}
            />
          </div>
        )}
      </Section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Journée en stage                                                            */
/* -------------------------------------------------------------------------- */

const ACCOMPAGNEMENTS = [
  { valeur: 'yes', libelle: 'Oui, en permanence' },
  { valeur: 'sometimes', libelle: 'Par moments' },
  { valeur: 'no', libelle: 'Non' },
]

export function OngletJournee({
  valeur, onChange, messages, enfant, anciennes, decisionInclusion,
}: PropsOnglet<VieEnStage> & {
  anciennes: AnciennesReponses
  /** La décision de l'équipe sur la demande d'inclusion : elle s'enregistre seule, hors de l'onglet. */
  decisionInclusion?: ReactNode
}) {
  const maj = (champs: Partial<VieEnStage>) => onChange({ ...valeur, ...champs })
  const accompagnements = ACCOMPAGNEMENTS.some(a => a.valeur === valeur.accompagnementDedie) || !valeur.accompagnementDedie
    ? ACCOMPAGNEMENTS
    : [...ACCOMPAGNEMENTS, { valeur: valeur.accompagnementDedie, libelle: `« ${valeur.accompagnementDedie} » (ancienne réponse)` }]

  return (
    <div className="space-y-8">
      <Section titre="Les activités">
        <Interrupteur
          label={`${enfant} peut participer à toutes les activités proposées`}
          coche={valeur.participeATout} onChange={v => maj({ participeATout: v })}
        />
        {!valeur.participeATout && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ZoneTexte
              label={`Ce que ${enfant} ne peut pas faire, et pourquoi`} requis
              valeur={valeur.restrictions} onChange={v => maj({ restrictions: v })}
              cle="restrictions" messages={messages}
            />
          </div>
        )}
      </Section>

      <Section titre="L’eau">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Liste
              label="Niveau de natation" requis
              valeur={valeur.natation}
              onChange={v => maj({ natation: v as NiveauNatation | '' })}
              vide="— Non renseigné —"
              options={NIVEAUX_NATATION.map(n => ({ valeur: n, libelle: PHRASE_NATATION[n](enfant) }))}
              cle="natation" messages={messages}
            />
            {anciennes.natation && (
              <AncienneReponse>
                {anciennes.natation.saitNager ? 'sait nager' : 'ne sait pas nager'}
                {anciennes.natation.niveau ? `, niveau « ${anciennes.natation.niveau} »` : ''}. Gardée tant
                qu’aucun niveau n’est choisi.
              </AncienneReponse>
            )}
          </div>
          <Interrupteur label={`${enfant} a peur de l’eau`} coche={valeur.peurDeLEau} onChange={v => maj({ peurDeLEau: v })} />
        </div>
      </Section>

      <Section titre="Besoins particuliers">
        <Interrupteur
          label={`${enfant} a un handicap, un trouble ou des besoins spécifiques`}
          coche={valeur.besoinsSpecifiques} onChange={v => maj({ besoinsSpecifiques: v })}
        />
        {decisionInclusion}
        {valeur.besoinsSpecifiques && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ZoneTexte
              label="Quelle situation, quel trouble, quels besoins" requis
              valeur={valeur.situation} onChange={v => maj({ situation: v })} cle="situation" messages={messages}
            />
            <ZoneTexte label="Ce que cela change dans une journée de groupe" valeur={valeur.impact} onChange={v => maj({ impact: v })} />
            <Liste
              label="Un accompagnement dédié est-il nécessaire ?"
              valeur={valeur.accompagnementDedie}
              onChange={v => maj({ accompagnementDedie: v as VieEnStage['accompagnementDedie'] })}
              vide="— Non renseigné —"
              options={accompagnements}
            />
            {valeur.accompagnementDedie && valeur.accompagnementDedie !== 'no' ? (
              <Saisie
                label="À quels moments, et pour quoi faire"
                valeur={valeur.accompagnementDetails} onChange={v => maj({ accompagnementDetails: v })}
              />
            ) : <div />}
            <ZoneTexte label="Ce qui fonctionne avec lui ou elle" valeur={valeur.strategies} onChange={v => maj({ strategies: v })} />
            <ZoneTexte label="Matériel ou aides à prévoir" valeur={valeur.materiel} onChange={v => maj({ materiel: v })} />
            <ZoneTexte
              label={`Comment on voit que ${enfant} ne va pas bien`}
              valeur={valeur.signauxDeStress} onChange={v => maj({ signauxDeStress: v })}
            />
            <ZoneTexte label="Ce qui lui plaît et ce qui lui réussit" valeur={valeur.forces} onChange={v => maj({ forces: v })} />
            <ZoneTexte
              label="Expériences précédentes en collectivité"
              valeur={valeur.experiences} onChange={v => maj({ experiences: v })}
            />
          </div>
        )}
      </Section>

      <Section titre="Autre chose ?">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ZoneTexte
            label={`Ce que l’équipe devrait savoir sur ${enfant}`}
            valeur={valeur.autresInfos} onChange={v => maj({ autresInfos: v })}
          />
        </div>
      </Section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

export interface PersonneAutorisee {
  id: string
  first_name: string
  last_name: string
  phone_number: string | null
  relationship: string | null
}

export function OngletSorties({
  valeur, onChange, messages, enfant, personnes, anciennes,
}: PropsOnglet<Sorties> & { personnes: PersonneAutorisee[]; anciennes: AnciennesReponses }) {
  const maj = (champs: Partial<Sorties>) => onChange({ ...valeur, ...champs })
  const basculer = (id: string) =>
    maj({
      personnesIds: valeur.personnesIds.includes(id)
        ? valeur.personnesIds.filter(p => p !== id)
        : [...valeur.personnesIds, id],
    })
  const inconnues = valeur.personnesIds.filter(id => !personnes.some(p => p.id === id)).length

  return (
    <div className="space-y-8">
      <Section titre="Qui vient le ou la chercher">
        <Interrupteur label={`${enfant} peut rentrer seul`} coche={valeur.partSeul} onChange={v => maj({ partSeul: v })} />
        {valeur.partSeul ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Saisie
                label="À partir de quelle heure" requis type="time"
                valeur={valeur.heureDepart} onChange={v => maj({ heureDepart: v })}
                cle="heureDepart" messages={messages}
              />
              {anciennes.heureDepart && <AncienneReponse>{anciennes.heureDepart}</AncienneReponse>}
            </div>
          </div>
        ) : (
          <div>
            <Libelle requis>Personnes autorisées</Libelle>
            {personnes.length === 0 ? (
              <p className="text-sm text-gray-500">
                Le parent n’a enregistré aucune personne autorisée. Il les ajoute depuis la fiche de son enfant
                (« Qui vient le ou la chercher »).
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {personnes.map(p => (
                  <li key={p.id}>
                    <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={valeur.personnesIds.includes(p.id)}
                        onChange={() => basculer(p.id)}
                        className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="flex-1 text-sm">
                        <span className="font-medium text-gray-900">{p.first_name} {p.last_name}</span>
                        {p.relationship && <span className="text-gray-500"> · {p.relationship}</span>}
                      </span>
                      {p.phone_number && <span className="text-sm text-gray-500">{p.phone_number}</span>}
                    </label>
                  </li>
                ))}
              </ul>
            )}
            {inconnues > 0 && (
              <Aide>
                {inconnues === 1 ? 'Une personne cochée a été supprimée' : `${inconnues} personnes cochées ont été supprimées`} du
                compte du parent.
              </Aide>
            )}
            <Message messages={messages} cle="personnesIds" />
            <Aide>Les personnes sont celles du compte du parent ; il les ajoute et les modifie lui-même.</Aide>
          </div>
        )}
      </Section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Autorisations et RGPD                                                       */
/* -------------------------------------------------------------------------- */

/** La ligne `kid_image_rights` en base : qui a répondu, quand, contre quelle politique. */
export interface ReponsesImage {
  consented_at: string | null
  consented_by: string | null
  policy_version: string | null
}

export interface EvenementConsentement {
  id: string
  occurred_at: string
  scope: string
  value: boolean
  actor_user_id: string | null
  source: string | null
}

export function OngletAutorisations({
  valeur, onChange, messages, enfant, parentId, reponsesImage, anciennes, imageModifiee,
  reponsesDuParent, onReponsesDuParent, journal,
}: PropsOnglet<Autorisations> & {
  parentId: string
  reponsesImage: ReponsesImage | null
  anciennes: AnciennesReponses
  imageModifiee: boolean
  reponsesDuParent: boolean
  onReponsesDuParent: (v: boolean) => void
  journal: EvenementConsentement[]
}) {
  const repondre = (cle: keyof Autorisations['image'], reponse: Reponse) =>
    onChange({ ...valeur, image: { ...valeur.image, [cle]: reponse } })
  const parLeParent = !!reponsesImage && reponsesImage.consented_by === parentId
  const qui = (acteur: string | null) => (acteur === parentId ? 'le parent' : 'l’équipe')

  return (
    <div className="space-y-8">
      <Section titre="En cas de problème de santé">
        <details className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <summary className="cursor-pointer font-medium">Le texte de l’accord (formulation de l’ONE)</summary>
          <p className="mt-2 whitespace-pre-line leading-relaxed">{ACCORD_SOINS}</p>
        </details>
        <Interrupteur
          label="Le parent a donné son accord pour les soins"
          coche={valeur.soins} onChange={v => onChange({ ...valeur, soins: v })}
          cle="soins" messages={messages}
        />
      </Section>

      <Section
        titre="Photos et vidéos"
        intro="Cinq autorisations indépendantes, données par le parent. L’équipe ne les modifie qu’à sa demande (courriel, formulaire signé, appel)."
      >
        {reponsesImage ? (
          <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600">
            Réponses enregistrées
            {reponsesImage.consented_at ? ` le ${dateFr(reponsesImage.consented_at)}` : ''}
            {' '}par {parLeParent ? 'le parent' : 'l’équipe'}
            {reponsesImage.policy_version ? ` · politique de confidentialité version ${reponsesImage.policy_version}` : ''}.
          </p>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <p className="font-medium">Le parent n’a pas encore répondu à ces cinq questions.</p>
            <p className="mt-1">
              La fiche date d’avant le formulaire actuel ; elles lui seront posées à sa prochaine vérification.
              {anciennes.photoConsent !== null && (
                <> Ancien accord global (site, réseaux sociaux, rapport annuel) : <strong>{anciennes.photoConsent ? 'oui' : 'non'}</strong>.</>
              )}
            </p>
          </div>
        )}

        <div className="space-y-2">
          {DESCRIPTIONS.map(d => (
            <div key={d.cle} className="rounded-lg border border-gray-200 p-3 sm:flex sm:items-start sm:gap-6">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-600">
                    {d.lettre}
                  </span>
                  {d.titre}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-gray-500">{d.exemple}</p>
              </div>
              <div className="mt-2 sm:mt-0">
                <OuiNon valeur={valeur.image[d.cle]} onChange={v => repondre(d.cle, v)} cle={d.cle} messages={messages} />
              </div>
            </div>
          ))}
        </div>

        <Case
          coche={valeur.image.accordAutreParent}
          onChange={v => onChange({ ...valeur, image: { ...valeur.image, accordAutreParent: v } })}
          label="Le parent confirme disposer de l’accord de l’autre parent ou du second titulaire de l’autorité parentale."
          aide="Requis dès qu’une publication hors de l’espace des familles (B à E) est autorisée."
          cle="accordAutreParent" messages={messages}
        />

        {imageModifiee && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
            <Case
              coche={reponsesDuParent}
              onChange={onReponsesDuParent}
              label={`Ces réponses m’ont été transmises par le parent de ${enfant}. L’enregistrement est tracé à mon nom dans le journal des consentements.`}
              aide={`Le parent est alors tenu pour avoir pris connaissance de la politique de confidentialité (version ${VERSION_POLITIQUE}) : elle ne lui sera pas redemandée.`}
              cle="reponsesDuParent" messages={messages}
            />
          </div>
        )}
      </Section>

      <Section titre="Vos données (politique de confidentialité)">
        <div className="flex items-start gap-2 text-sm text-gray-700">
          <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
          {reponsesImage ? (
            <p>
              Le parent a pris connaissance de la politique de confidentialité
              {reponsesImage.policy_version ? ` (version ${reponsesImage.policy_version})` : ''}
              {parLeParent
                ? ' en répondant à ces questions.'
                : ' : ses réponses, transmises à l’équipe, ont été enregistrées par elle.'}
            </p>
          ) : (
            <p>
              Pas encore confirmée par le parent : la confirmation lui est demandée avec les autorisations d’image.
            </p>
          )}
        </div>
      </Section>

      <Section titre="Historique des autorisations d’image">
        {journal.length === 0 ? (
          <p className="text-sm text-gray-500">Aucune réponse enregistrée.</p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
            {journal.map(e => (
              <li key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                <span className="w-24 text-gray-500">{dateFr(e.occurred_at)}</span>
                <span className="flex-1 text-gray-900">{libelleConsentement(e.scope)}</span>
                <span className={e.value ? 'font-medium text-green-700' : 'font-medium text-gray-700'}>
                  {e.value ? 'Oui' : 'Non'}
                </span>
                <span className="text-xs text-gray-500">par {qui(e.actor_user_id)}</span>
              </li>
            ))}
          </ul>
        )}
        <Aide>Journal en ajout seul, tenu par la base : chaque réponse qui change y laisse une ligne.</Aide>
      </Section>
    </div>
  )
}

